import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { CustomerChatWidget } from '../components/CustomerChatWidget';
import { PortalAnalyticsTracker } from '../components/PortalAnalyticsTracker';
import { InstallBanner } from '../components/InstallBanner';
import { CustomerBugTracker } from '../components/CustomerBugTracker';
import { CustomerPortalTutorial } from '../components/CustomerPortalTutorial';
import { 
  Home, Upload, MessageSquare, LogOut, User, Menu, X, 
  FileImage, Star, Bell, Settings, ChevronRight, TicketIcon, Calendar, Shield, Phone, LifeBuoy
} from 'lucide-react';
import { toast } from 'sonner';

// Company contact details shown in the portal header
const SUPPORT_PHONE = '1-587-388-0555';
const SUPPORT_TOLLFREE = '1-XXX-XXX-XXXX';

/**
 * CustomerPortalLayout - Separate layout for customer-facing portal
 * This layout is completely isolated from the main CRM
 * Only accessible by users with "customer" role
 */
const CustomerPortalLayout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Security check - redirect non-customers away
  useEffect(() => {
    if (user && user.role !== 'customer') {
      // CRM users should not access customer portal directly
      // They can view it via admin preview mode
      if (!location.pathname.includes('/preview')) {
        toast.error('Access denied - Customer portal is for customers only');
        navigate('/dashboard');
      }
    }
  }, [user, navigate, location]);

  const handleLogout = () => {
    logout();
    navigate('/customer-login');
    toast.success('Logged out successfully');
  };

  const navItems = [
    { label: 'Home', path: '/my-portal', icon: Home },
    { label: 'Support', path: '/my-portal/support', icon: TicketIcon },
    { label: 'Warranty Claims', path: '/my-portal/warranties', icon: Shield },
    { label: 'Book Meeting', path: '/my-portal/book-meeting', icon: Calendar },
    { label: 'My Uploads', path: '/my-portal/files', icon: Upload },
    { label: 'My Testimonials', path: '/my-portal/testimonials', icon: MessageSquare },
    { label: 'Resources', path: '/my-portal/resources', icon: LifeBuoy },
    { label: 'Notifications', path: '/my-portal/notifications', icon: Bell },
  ];

  const isActive = (path) => {
    if (path === '/my-portal') {
      return location.pathname === '/my-portal';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#001828] font-['Manrope']">
      {/* Top Navigation Bar */}
      <header className="bg-[#001828]/85 backdrop-blur-md border-b border-[#00486D] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-white/5 text-white"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
              <Link to="/my-portal" className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_66080daa-dc30-4cf8-b53e-8e5d6f014e3b/artifacts/c014elu5_image.png"
                  alt="Chinook Manufacturing"
                  className="h-9 w-auto object-contain"
                />
              </Link>
            </div>

            {/* User Menu */}
            <div className="flex items-center gap-4">
              {/* Contact phone numbers */}
              <div className="hidden md:flex flex-col items-end leading-tight" data-testid="header-phone-numbers">
                <a href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`} className="flex items-center gap-1.5 text-sm font-medium text-white hover:text-[#00C0C0] transition-colors" data-testid="header-phone-main">
                  <Phone className="h-3.5 w-3.5 text-[#00C0C0]" />
                  {SUPPORT_PHONE}
                </a>
                <span className="text-xs text-[#94A3B8]" data-testid="header-phone-tollfree">
                  Toll Free: {SUPPORT_TOLLFREE}
                </span>
              </div>
              <CustomerBugTracker />
              <div className="hidden sm:flex items-center gap-2 text-sm text-[#94A3B8]">
                <User className="h-4 w-4 text-[#00C0C0]" />
                <span>{user?.first_name} {user?.last_name}</span>
              </div>
              <Button variant="ghost" size="sm" onClick={handleLogout} className="text-[#94A3B8] hover:text-white hover:bg-white/5">
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <aside className={`
            ${sidebarOpen ? 'block' : 'hidden'} lg:block
            fixed lg:relative inset-0 lg:inset-auto z-40 lg:z-auto
            w-64 lg:w-56 bg-[#001828] lg:bg-transparent
            pt-20 lg:pt-0 px-4 lg:px-0
          `}>
            {/* Mobile overlay */}
            {sidebarOpen && (
              <div 
                className="fixed inset-0 bg-black/50 lg:hidden -z-10"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            
            <nav className="space-y-1 bg-[#002036] rounded-2xl p-3 shadow-lg border border-[#00486D]">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium
                      transition-all duration-200
                      ${active 
                        ? 'bg-[#00C0C0]/15 text-[#00D0E0] border-l-4 border-[#00C0C0]' 
                        : 'text-[#94A3B8] hover:bg-white/5 hover:text-white'
                      }
                    `}
                    data-testid={`portal-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  >
                    <Icon className={`h-5 w-5 ${active ? 'text-[#00C0C0]' : 'text-slate-500'}`} />
                    {item.label}
                    {active && <ChevronRight className="h-4 w-4 ml-auto" />}
                  </Link>
                );
              })}
            </nav>

            {/* Help Card */}
            <div className="mt-6 bg-gradient-to-br from-[#00C0C0] to-[#007A8A] rounded-2xl p-4 text-[#001828]">
              <h3 className="font-semibold mb-2 text-[#001828]">Need Help?</h3>
              <p className="text-sm text-[#00303f] mb-3">
                Contact our support team for assistance with your equipment, claims, or account.
              </p>
              <Button 
                variant="secondary" 
                size="sm" 
                className="w-full bg-[#001828] hover:bg-[#002036] text-white border-0"
                onClick={() => { setSidebarOpen(false); navigate('/my-portal/support'); }}
                data-testid="contact-support-btn"
              >
                <TicketIcon className="h-4 w-4 mr-2" />
                Contact Support
              </Button>
              <a
                href={`tel:${SUPPORT_PHONE.replace(/[^0-9+]/g, '')}`}
                className="mt-2 flex items-center justify-center gap-1.5 text-sm text-[#00303f] hover:text-[#001828] font-medium"
                data-testid="contact-support-phone"
              >
                <Phone className="h-3.5 w-3.5" />
                {SUPPORT_PHONE}
              </a>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            <div className="bg-slate-50 rounded-2xl border border-slate-200 p-4 sm:p-6 min-h-[70vh] shadow-sm">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      
      {/* Customer Portal Tutorial */}
      <CustomerPortalTutorial />
      
      {/* Customer Chat Widget */}
      <CustomerChatWidget />

      {/* Privacy-safe portal usage analytics */}
      <PortalAnalyticsTracker />

      {/* Auto install prompt (one-tap on Android/desktop, guided on iPhone) */}
      <InstallBanner />
    </div>
  );
};

export default CustomerPortalLayout;
